import type { AxiosError } from 'axios';

import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { api } from '../../api';
import { environment } from '../../environment/environment';
import type { Advocate } from '../../models/advocate';
import { GUEST_COMPONENT } from '../../models/chat';
import type { ErrorMessage } from '../../models/Error';
import type { GuestResource } from '../../models/guest';
import type { AsyncThunkConfig } from '../../models/slice';
import { RaygunErrorHandlerService } from '../../services/raygun';

const { logError } = RaygunErrorHandlerService();

type LepsSliceType = {
  lepsPageStatus: GUEST_COMPONENT;
  lepsAdvocates: Advocate[];
  externalLepsAdvocate: Advocate[];
  searchLepsAdvocatesLoading: boolean;
  lepsResources: GuestResource[];
  lepsResourcesLoading: boolean;
  showLepsTabBar: boolean;
};

const initialState: LepsSliceType = {
  lepsPageStatus: GUEST_COMPONENT.ADVOCATE_SELECT,
  lepsAdvocates: [],
  externalLepsAdvocate: [],
  searchLepsAdvocatesLoading: false,
  lepsResources: [],
  lepsResourcesLoading: false,
  showLepsTabBar: true,
};

export type FindAdvocatesBody = {
  for_service_providers: boolean;
  label_ids: string[];
  elm_app_major_version: string;
  elm_app_version: string;
};

type SearchLepsAdvocate = { internalAdvocates: Advocate[]; externalAdvocates: Advocate[] };

export const searchLepsAdvocates = createAsyncThunk<SearchLepsAdvocate, undefined, AsyncThunkConfig>(
  'leps/searchLepsAdvocates',
  async (_, thunkAPI) => {
    try {
      const searchEndpoint = '/guest/v2_search_advocates';
      const appOrgCode = thunkAPI.getState().appStatusSlice.appOrgCode;
      const body: FindAdvocatesBody = {
        label_ids: thunkAPI.getState().guestSlice.guestLabelIds,
        for_service_providers: false,
        elm_app_major_version: environment.versionNumber.split('.')[0],
        elm_app_version: environment.versionNumber,
      };
      const response = await api.post<Advocate[]>(searchEndpoint, body);
      return {
        internalAdvocates: response?.filter((advocate) => advocate.org_code === appOrgCode) ?? [],
        externalAdvocates: response?.filter((advocate) => advocate.org_code !== appOrgCode) ?? [],
      };
    } catch (e) {
      logError(e, ['lepsSlice', 'searchLepsAdvocates']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const getLEPSResources = createAsyncThunk<GuestResource[], undefined, AsyncThunkConfig>(
  'leps/getLEPSResources',
  async (_, thunkAPI) => {
    try {
      const response = await api.post<GuestResource[]>('/guest/v1_get_leps_resources');
      return response ?? [];
    } catch (e) {
      logError(e, ['lepsSlice', 'getLEPSResources']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const lepsSlice = createSlice({
  name: 'leps',
  initialState,
  reducers: {
    setLepsPageStatus: (state, action: PayloadAction<GUEST_COMPONENT>) => {
      state.lepsPageStatus = action.payload;
    },
    setShowLepsTabBar: (state, action: PayloadAction<boolean>) => {
      state.showLepsTabBar = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchLepsAdvocates.pending, (state, _action) => {
        state.searchLepsAdvocatesLoading = true;
      })
      .addCase(searchLepsAdvocates.fulfilled, (state, action) => {
        state.lepsAdvocates = action.payload.internalAdvocates;
        state.externalLepsAdvocate = action.payload.externalAdvocates;
        state.searchLepsAdvocatesLoading = false;
      })
      .addCase(searchLepsAdvocates.rejected, (state, _action) => {
        state.searchLepsAdvocatesLoading = false;
      })
      .addCase(getLEPSResources.pending, (state, _action) => {
        state.lepsResourcesLoading = true;
      })
      .addCase(getLEPSResources.fulfilled, (state, action) => {
        state.lepsResources = action.payload;
        state.lepsResourcesLoading = false;
      });
  },
});

export const { setLepsPageStatus, setShowLepsTabBar } = lepsSlice.actions;
